import React, { useMemo } from 'react';
import { Calendar } from './ui/calendar';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const WeekCalendar = ({ weekStart, weekEnd, onWeekSelect }) => {
  const startDate = useMemo(() => new Date(weekStart + 'T00:00:00'), [weekStart]);
  const endDate = useMemo(() => new Date(weekEnd + 'T00:00:00'), [weekEnd]);

  const selectedRange = useMemo(() => ({
    from: startDate,
    to: endDate
  }), [startDate, endDate]);

  const defaultMonth = useMemo(() => startDate, [startDate]);

  const handleDayClick = (day) => {
    if (!day || !onWeekSelect) return;
    const dayOfWeek = day.getDay();
    const sunday = new Date(day);
    sunday.setDate(day.getDate() - dayOfWeek);
    sunday.setHours(0, 0, 0, 0);
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);

    const fmt = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };
    onWeekSelect(fmt(sunday), fmt(saturday));
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4" data-testid="week-calendar">
      <Calendar
        mode="range"
        selected={selectedRange}
        month={defaultMonth}
        onMonthChange={() => {}}
        onDayClick={handleDayClick}
        showOutsideDays={true}
        weekStartsOn={0}
        classNames={{
          months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
          month: "space-y-3",
          caption: "flex justify-center pt-1 relative items-center",
          caption_label: "text-base font-bold text-brand-blue",
          nav: "space-x-1 flex items-center",
          nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 inline-flex items-center justify-center rounded-md border border-gray-200",
          nav_button_previous: "absolute left-1",
          nav_button_next: "absolute right-1",
          table: "w-full border-collapse",
          head_row: "flex",
          head_cell: "text-gray-500 rounded-md w-10 font-semibold text-[0.75rem] uppercase",
          row: "flex w-full mt-1",
          cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-blue-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-blue-50",
          day: "h-10 w-10 p-0 font-normal cursor-pointer hover:bg-gray-100 rounded-md inline-flex items-center justify-center aria-selected:opacity-100",
          day_range_start: "day-range-start rounded-l-md !bg-brand-blue !text-white font-bold",
          day_range_end: "day-range-end rounded-r-md !bg-brand-blue !text-white font-bold",
          day_selected: "bg-brand-blue text-white hover:bg-brand-blue hover:text-white focus:bg-brand-blue focus:text-white",
          day_today: "border-2 border-brand-orange font-bold text-brand-orange",
          day_outside: "day-outside text-gray-300 aria-selected:bg-blue-50 aria-selected:text-gray-400",
          day_disabled: "text-gray-300 opacity-50",
          day_range_middle: "aria-selected:bg-blue-100 aria-selected:text-brand-blue font-semibold",
          day_hidden: "invisible",
        }}
        components={{
          IconLeft: ({ className, ...props }) => (
            <ChevronLeft className="h-4 w-4" {...props} />
          ),
          IconRight: ({ className, ...props }) => (
            <ChevronRight className="h-4 w-4" {...props} />
          ),
        }}
      />
    </div>
  );
};

export default WeekCalendar;
